
import { partyHelpers } from '../../../mocks/equipmentHireCatalogue';
import { useScrollAnimation, useStaggeredAnimation } from '../../../hooks/useScrollAnimation';

export default function PartyHelpersSection() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible, getItemDelay } = useStaggeredAnimation(
    partyHelpers.length,
    { staggerDelay: 100 }
  );

  return (
    <section id="party-helpers" className="py-20 bg-stone-50">
      <div className="max-w-6xl mx-auto px-6">
        <div
          ref={titleRef}
          className={`text-center mb-14 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 mb-4">
            <i className="ri-service-line text-amber-600 text-sm"></i>
            <span className="text-amber-700 text-xs tracking-widest uppercase font-semibold">
              Party Rentals
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-stone-900 mb-4">
            <strong>Party Helpers</strong>
          </h2>
          <p className="text-stone-500 text-sm max-w-xl mx-auto leading-relaxed">
            Ice buckets, punch bowls, beverage dispensers, water bottles, grazing table stands,
            cheese boards and knives — all the finishing touches for a flawless event.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {partyHelpers.map((item, index) => (
            <div
              key={item.id}
              style={getItemDelay(index)}
              className={`bg-white rounded-lg overflow-hidden border border-stone-100 hover:border-amber-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-500 group ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="relative w-full h-52 overflow-hidden bg-stone-100">
                <img
                  src={item.image}
                  alt={`${item.name} hire Melbourne`}
                  title={`${item.name} party helper rental Melbourne`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
              </div>

              <div className="p-5">
                <h3 className="font-display text-lg text-stone-900 mb-1">{item.name}</h3>
                <p className="text-stone-500 text-xs leading-relaxed mb-4">{item.description}</p>

                <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                  <div>
                    <span className="text-xl font-bold text-amber-600">${item.price}</span>
                    <span className="text-stone-400 text-xs ml-1">{item.unit}</span>
                    {item.minQty > 1 && (
                      <p className="text-stone-400 text-[10px] mt-0.5">Min. {item.minQty} units</p>
                    )}
                  </div>
                  <a
                    href="#hire-inquiry"
                    className="px-4 py-2 bg-stone-900 text-white text-xs font-medium rounded-full hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Enquire
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Event Staff Banner */}
        <div className="mt-16 rounded-2xl overflow-hidden relative">
          <img
            src="https://public.readdy.ai/ai/img_res/8e6837ea1838bd201e278fc7d2f5b713.jpg"
            alt="FMR Event Staff Available"
            className="w-full h-64 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/60 to-transparent flex items-center">
            <div className="px-10 max-w-lg">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 mb-4">
                <i className="ri-user-star-line text-amber-300 text-xs"></i>
                <span className="text-amber-200 text-xs tracking-widest uppercase font-medium">Event Staff Available</span>
              </div>
              <h3 className="text-white font-display text-3xl mb-2">Professional, Certified,<br />Experienced &amp; Fun!!</h3>
              <p className="text-white/70 text-sm mb-5">Our team brings energy and expertise to every event. Bartenders, waitstaff, mixologists and more — all fully trained and RSA certified.</p>
              <a
                href="#hire-inquiry"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-stone-900 font-semibold rounded-full hover:from-amber-300 hover:to-amber-400 transition-all cursor-pointer whitespace-nowrap text-sm shadow-lg shadow-amber-500/30"
              >
                <i className="ri-team-line"></i>Book Event Staff
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
